import api from './api.service';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ApiBody<T>;
  if (body?.data === undefined) throw new Error(body?.message || 'WBS library request failed');
  return body.data;
}

export const epcWbsLibraryService = {
  listLibraries: () => unwrap<any[]>(api.get('/epc/wbs-libraries')),
  createLibrary: (body: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/epc/wbs-libraries', body)),
  getLibrary: (libraryId: string) => unwrap<unknown>(api.get(`/epc/wbs-libraries/${libraryId}`)),
  getLibraryAnalytics: (libraryId: string) =>
    unwrap<{
      packageCount: number;
      wbsNodeCount: number;
      costTemplateCount: number;
      estimateCount: number;
      revisionCount: number;
      topPackages: Array<{
        packageId: string;
        code: string;
        name: string;
        estimatePlugCount: number;
        nodeCount: number;
        costTemplateCount: number;
      }>;
    }>(api.get(`/epc/wbs-libraries/${libraryId}/analytics`)),
  patchLibrary: (libraryId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/wbs-libraries/${libraryId}`, body)),
  deleteLibrary: (libraryId: string) =>
    unwrap<unknown>(api.delete(`/epc/wbs-libraries/${libraryId}`)),

  listPackages: (libraryId: string) =>
    unwrap<any[]>(api.get(`/epc/wbs-libraries/${libraryId}/packages`)),
  createPackage: (libraryId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.post(`/epc/wbs-libraries/${libraryId}/packages`, body)),
  getPackage: (packageId: string) =>
    unwrap<unknown>(api.get(`/epc/wbs-libraries/packages/${packageId}`)),
  patchPackage: (packageId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/wbs-libraries/packages/${packageId}`, body)),
  deletePackage: (packageId: string) =>
    unwrap<unknown>(api.delete(`/epc/wbs-libraries/packages/${packageId}`)),
  duplicatePackage: (packageId: string, body?: Record<string, unknown>) =>
    unwrap<unknown>(api.post(`/epc/wbs-libraries/packages/${packageId}/duplicate`, body || {})),
  publishPackage: (packageId: string) =>
    unwrap<unknown>(api.post(`/epc/wbs-libraries/packages/${packageId}/publish`, {})),
  revisePackage: (packageId: string) =>
    unwrap<unknown>(api.post(`/epc/wbs-libraries/packages/${packageId}/revise`, {})),
  exportPackage: async (packageId: string) => {
    const res = await api.getClient().get(`/epc/wbs-libraries/packages/${packageId}/export`, {
      responseType: 'blob',
    });
    return res.data as Blob;
  },
  importPackage: async (libraryId: string, file: File, packageCode: string, packageName: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('packageCode', packageCode);
    fd.append('packageName', packageName);
    return unwrap(
      api.post(`/epc/wbs-libraries/${libraryId}/packages/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    );
  },

  listPackageNodes: (packageId: string) =>
    unwrap<any[]>(api.get(`/epc/wbs-libraries/packages/${packageId}/nodes`)),
  createPackageNode: (packageId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.post(`/epc/wbs-libraries/packages/${packageId}/nodes`, body)),
  patchPackageNode: (packageId: string, nodeId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/wbs-libraries/packages/${packageId}/nodes/${nodeId}`, body)),
  deletePackageNode: (packageId: string, nodeId: string) =>
    unwrap<unknown>(api.delete(`/epc/wbs-libraries/packages/${packageId}/nodes/${nodeId}`)),
  batchReorderPackageNodes: (
    packageId: string,
    updates: Array<{ id: string; parentId?: string | null; sortOrder: number }>
  ) =>
    unwrap<{ updated: number }>(
      api.patch(`/epc/wbs-libraries/packages/${packageId}/nodes/reorder`, { updates })
    ),

  listCostTemplates: (packageId: string, libraryNodeId?: string) =>
    unwrap<any[]>(
      api.get(`/epc/wbs-libraries/packages/${packageId}/cost-templates`, {
        params: libraryNodeId ? { libraryNodeId } : undefined,
      })
    ),
  createCostTemplate: (packageId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.post(`/epc/wbs-libraries/packages/${packageId}/cost-templates`, body)),
  patchCostTemplate: (packageId: string, templateId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(
      api.patch(`/epc/wbs-libraries/packages/${packageId}/cost-templates/${templateId}`, body)
    ),
  deleteCostTemplate: (packageId: string, templateId: string) =>
    unwrap<unknown>(
      api.delete(`/epc/wbs-libraries/packages/${packageId}/cost-templates/${templateId}`)
    ),

  listCbsCodes: () => unwrap<any[]>(api.get('/epc/cbs-codes')),
  listLabourResourceClasses: () =>
    unwrap<any[]>(api.get('/epc/wbs-libraries/resource-classes/labour')),
  listEquipmentResourceClasses: () =>
    unwrap<any[]>(api.get('/epc/wbs-libraries/resource-classes/equipment')),
  createCbsCode: (body: Record<string, unknown>) => unwrap<unknown>(api.post('/epc/cbs-codes', body)),
  patchCbsCode: (cbsId: string, body: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/epc/cbs-codes/${cbsId}`, body)),
  deleteCbsCode: (cbsId: string) => unwrap<unknown>(api.delete(`/epc/cbs-codes/${cbsId}`)),

  plugLibraryPackage: (
    estimateId: string,
    body: {
      revisionId: string;
      clientBoqLineId: string;
      libraryPackageId: string;
      parentWbsNodeId?: string;
      codePrefix?: string;
      force?: boolean;
    }
  ) => unwrap<unknown>(api.post(`/epc/estimations/${estimateId}/plug-library-package`, body)),

  seedDefaultLibrary: () => unwrap<unknown>(api.post('/epc/wbs-libraries/seed', {})),

  getPackageReadiness: (packageId: string) =>
    unwrap<{
      hasPackageLayerNode: boolean;
      packageNodesWithoutTemplates: string[];
      packageNodesMissingTemplates: Array<{ id: string; code: string }>;
      orphanNodes: string[];
      templateCount: number;
      rolledUpDefaultCost: number;
      isPlugReady: boolean;
      plugPreview: {
        wbsNodeCount: number;
        packageLayerCount: number;
        costTemplateCount: number;
      };
      cbsBreakdown: Array<{ cbsCode: string; amount: number; lineCount: number }>;
    }>(api.get(`/epc/wbs-libraries/packages/${packageId}/readiness`)),

  getPackageUsage: (packageId: string) =>
    unwrap<{
      estimateCount: number;
      revisionCount: number;
      wbsNodeCount: number;
      recentEstimates: Array<{
        estimateId: string;
        estimateNumber: string;
        title: string | null;
        tenderId: string;
        revisionCount: number;
        lastPluggedAt: string | null;
      }>;
    }>(api.get(`/epc/wbs-libraries/packages/${packageId}/usage`)),

  promoteFromEstimate: (
    estimateId: string,
    body: {
      revisionId: string;
      libraryId: string;
      packageCode: string;
      packageName: string;
      rootWbsNodeId: string;
      includeCostLines?: boolean;
    }
  ) => unwrap<unknown>(api.post(`/epc/estimations/${estimateId}/promote-to-library`, body)),
};
